import { supabase } from "./supabase";
import { env } from "./env";

export type AdminRole = "super_admin" | "admin" | "viewer";

export interface AdminTreatment {
  id: string;
  medication: string;
  status: string;
  plan_duration_months: number | null;
  monthly_price: number | null;
  started_at: string | null;
}

export interface AdminUserRow {
  id: string;
  email: string | null;
  phone: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  email_confirmed_at: string | null;
  banned_until: string | null;
  patient: {
    id: string;
    first_name: string;
    last_name: string;
    phone: string | null;
    archived_at: string | null;
    archived_reason: string | null;
    created_at: string;
  } | null;
  treatments: AdminTreatment[];
}

export interface AdminAnalytics {
  total_users: number;
  new_30d: number;
  new_this_month: number;
  new_last_month: number;
  deleted_total: number;
  deleted_this_month: number;
  deleted_last_month: number;
  monthly: Array<{ month: string; new: number; deleted: number }>;
}

export interface MonthlyRevenueRow {
  month: string;
  active_subscriptions: number;
  mrr_cents: number;
  new_subscriptions: number;
  canceled_subscriptions: number;
}

export interface ProgramRow {
  program: string;
  user_count: number;
  active_count: number;
}

export interface CouponUsageRow {
  coupon_code: string;
  month: string;
  redemption_count: number;
  total_discount_cents: number;
}

export interface TicketAnalytics {
  current: {
    open: number;
    awaiting_patient: number;
    resolved_total: number;
    unassigned_active: number;
  };
  month_over_month: {
    created_this_month: number;
    created_last_month: number;
    resolved_this_month: number;
    resolved_last_month: number;
    pct_change_created: number | null;
    pct_change_resolved: number | null;
  };
  monthly: Array<{ month: string; created: number; resolved: number }>;
  by_tag: Array<{ name: string; color: string | null; count: number }>;
  time_to_close: {
    this_month: { avg_hours: number | null; median_hours: number | null; count: number };
    all_time:   { avg_hours: number | null; median_hours: number | null; count: number };
  };
  by_admin: Array<{ admin_id: string; display_name: string; email: string | null; count: number }>;
}

export interface AdminRoleRow {
  user_id: string;
  email: string | null;
  role: AdminRole;
  granted_at: string;
  granted_by: string | null;
  notes: string | null;
  last_sign_in_at: string | null;
}

export interface AuditLogRow {
  id: string;
  occurred_at: string;
  actor_id: string | null;
  actor_email: string | null;
  actor_role: string | null;
  action: string;
  table_name: string | null;
  record_id: string | null;
  patient_id: string | null;
  context: Record<string, unknown> | null;
}

export interface AuditLogFilters {
  actor_id?: string;
  actor_role?: string;
  action?: string;
  table_name?: string;
  date_from?: string;  // ISO datetime
  date_to?: string;
  search?: string;
}

export interface AuditLogResponse {
  rows: AuditLogRow[];
  total: number;
  limit: number;
  offset: number;
  distinct: {
    actions: string[];
    tables: string[];
    roles: string[];
  };
}

export interface CouponRow {
  code: string;
  name: string | null;
  percent_off: number | null;
  amount_off_cents: number | null;
  applies_to: string | null;
  valid_from: string | null;
  valid_until: string | null;
  max_redemptions: number | null;
  stripe_coupon_id: string | null;
  active: boolean;
  created_by: string | null;
  created_at: string;
}

async function callAdmin<T>(action: string, body: Record<string, unknown> = {}): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("Not signed in");

  const res = await fetch(`${env.VITE_SUPABASE_URL}/functions/v1/admin-users`, {
    method: "POST",
    headers: {
      authorization: `Bearer ${token}`,
      "content-type": "application/json",
    },
    body: JSON.stringify({ action, ...body }),
  });

  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new Error(err.message ?? err.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export const admin = {
  // Self-introspection
  myRole: () => callAdmin<{ role: AdminRole | null }>("my_role"),

  // User list + analytics
  listUsers: (include_archived = false) =>
    callAdmin<{ users: AdminUserRow[]; archived_count: number }>("list_users", { include_archived }),
  getAnalytics: () => callAdmin<AdminAnalytics>("get_analytics"),
  getRevenue: (months_back = 12) =>
    callAdmin<{ months: MonthlyRevenueRow[] }>("get_revenue", { months_back }),
  getUsersByProgram: () =>
    callAdmin<{ programs: ProgramRow[] }>("get_users_by_program"),
  getCouponUsage: (months_back = 12) =>
    callAdmin<{ usage: CouponUsageRow[] }>("get_coupon_usage", { months_back }),

  // Admin tier mutations
  resetPassword: (target_user_id: string) =>
    callAdmin<{ ok: true }>("reset_password", {
      target_user_id,
      redirect_to: `${window.location.origin}/auth/reset`,
    }),
  sendMagicLink: (target_user_id: string) =>
    callAdmin<{ ok: true }>("send_magic_link", {
      target_user_id,
      redirect_to: `${window.location.origin}/auth/confirm`,
    }),
  updateEmail: (target_user_id: string, new_email: string) =>
    callAdmin<{ ok: true }>("update_email", { target_user_id, new_email }),
  archiveUser: (target_user_id: string, reason?: string) =>
    callAdmin<{ ok: true }>("archive_user", { target_user_id, reason }),
  unarchiveUser: (target_user_id: string) =>
    callAdmin<{ ok: true }>("unarchive_user", { target_user_id }),

  // Super-admin only
  listAdmins: () => callAdmin<{ admins: AdminRoleRow[] }>("list_admins"),
  setAdminRole: (target_user_id: string, role: AdminRole, notes?: string) =>
    callAdmin<{ ok: true }>("set_admin_role", { target_user_id, role, notes }),
  removeAdminRole: (target_user_id: string) =>
    callAdmin<{ ok: true }>("remove_admin_role", { target_user_id }),
  deleteUser: (target_user_id: string) =>
    callAdmin<{ ok: true }>("delete_user", { target_user_id }),
  // Ticket analytics for the AdminAnalytics dashboard. Lives in its
  // own edge function (admin-ticket-analytics) so we don't bloat
  // admin-users' query graph.
  getTicketAnalytics: async (): Promise<TicketAnalytics> => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) throw new Error("Not signed in");
    const res = await fetch(`${env.VITE_SUPABASE_URL}/functions/v1/admin-ticket-analytics`, {
      method: "POST",
      headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
      body: "{}",
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
      throw new Error(err.message ?? err.error ?? `HTTP ${res.status}`);
    }
    return res.json() as Promise<TicketAnalytics>;
  },

  // Grant admin access by email. If no auth user exists for that email,
  // creates one + sends an invite, then upserts the admin_roles row.
  // Lives in its own edge function so we don't bloat admin-users.
  grantAdminByEmail: async (
    email: string, role: AdminRole, notes?: string,
  ): Promise<{ ok: true; user_id: string; was_created: boolean; invited: boolean }> => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) throw new Error("Not signed in");
    const res = await fetch(`${env.VITE_SUPABASE_URL}/functions/v1/admin-grant-by-email`, {
      method: "POST",
      headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
      body: JSON.stringify({ email, role, notes }),
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
      throw new Error(err.message ?? err.error ?? `HTTP ${res.status}`);
    }
    return res.json() as Promise<{ ok: true; user_id: string; was_created: boolean; invited: boolean }>;
  },

  // Audit log read (super_admin only). Lives in its own edge function — see
  // admin-audit-log/index.ts for filter semantics + meta-audit behavior.
  getAuditLog: async (
    filters: AuditLogFilters = {},
    pagination: { limit?: number; offset?: number } = {},
  ): Promise<AuditLogResponse> => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) throw new Error("Not signed in");
    const res = await fetch(`${env.VITE_SUPABASE_URL}/functions/v1/admin-audit-log`, {
      method: "POST",
      headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
      body: JSON.stringify({ filters, ...pagination }),
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
      throw new Error(err.message ?? err.error ?? `HTTP ${res.status}`);
    }
    return res.json() as Promise<AuditLogResponse>;
  },

  // Coupons (super_admin write, viewer+ read)
  listCoupons: () => callAdmin<{ coupons: CouponRow[] }>("list_coupons"),
  createCoupon: (input: Partial<CouponRow> & { code: string }) =>
    callAdmin<{ ok: true }>("create_coupon", input as Record<string, unknown>),
  updateCoupon: (code: string, patch: Partial<CouponRow>) =>
    callAdmin<{ ok: true }>("update_coupon", { code, ...patch }),
  deleteCoupon: (code: string) =>
    callAdmin<{ ok: true }>("delete_coupon", { code }),
};
